import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Users, Coins, ArrowRight, ShareNetwork, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtAone } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ReferralsPage({ onConnect }) {
    const { user } = useAuth();
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!user) return;
        api.get("/referrals/me").then((r) => setData(r.data)).catch(() => {});
    }, [user?.id]);

    if (!user) {
        return (
            <div className="min-h-screen px-5 sm:px-8 py-24 text-center">
                <div className="max-w-md mx-auto card p-8">
                    <ShareNetwork size={32} weight="duotone" className="text-[var(--primary-glow)] mx-auto mb-4" />
                    <h2 className="text-[22px] font-bold text-[var(--text)]">Sign in to see your referral code</h2>
                    <p className="text-[14px] text-[var(--text-2)] mt-3">
                        Earn 10% AONE commission on every plan purchased by miners you refer.
                    </p>
                    <button onClick={onConnect} className="btn-primary mt-6">Get started <ArrowRight size={13} /></button>
                </div>
            </div>
        );
    }

    if (!data) {
        return <div className="min-h-screen px-5 sm:px-8 py-24 text-center text-[var(--text-3)]">Loading…</div>;
    }

    const referralUrl = `${window.location.origin}/?ref=${data.code}`;

    return (
        <div className="min-h-screen">
            <section className="border-b bg-hero-gradient" style={{ borderColor: "var(--border)" }}>
                <div className="max-w-[1100px] mx-auto px-5 sm:px-8 pt-14 pb-10">
                    <div className="eyebrow mb-3">Referral Program</div>
                    <h1 className="text-[36px] sm:text-[52px] font-bold tracking-[-0.035em] text-[var(--text)] max-w-3xl leading-[1.05]">
                        Earn 10% AONE on every plan your referrals buy.
                    </h1>
                    <p className="mt-4 text-[16px] sm:text-[18px] text-[var(--text-2)] max-w-2xl leading-[1.55]">
                        Share your code. Anyone who signs up through it becomes your downline forever. Every time they buy a mining plan, {data.commission_pct}% of the USDT cost is paid to you in AONE - instantly, on-chain, no lockups.
                    </p>
                </div>
            </section>

            <section className="py-10 px-5 sm:px-8">
                <div className="max-w-[1100px] mx-auto grid md:grid-cols-3 gap-4 mb-6">
                    <StatCard label="Your Code" value={data.code} mono data-testid="ref-code-card" />
                    <StatCard label="Referred Miners" value={data.referral_count.toString()} icon={<Users size={16} />} />
                    <StatCard label="Total AONE Earned" value={fmtAone(data.earnings_aone, 2)} icon={<Coins size={16} />} />
                </div>

                {/* Share panel */}
                <div className="max-w-[1100px] mx-auto card p-6 sm:p-8">
                    <div className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-[var(--text-3)] mb-3">
                        Share your referral link
                    </div>
                    <div className="flex items-center gap-2 p-3 rounded-md" style={{ background: "var(--bg-raised)", border: "1px solid var(--border)" }}>
                        <code data-testid="ref-url" className="font-mono text-[13px] text-[var(--text)] break-all flex-1">
                            {referralUrl}
                        </code>
                        <button
                            onClick={() => { navigator.clipboard.writeText(referralUrl); toast.success("Referral link copied"); }}
                            className="p-2 rounded-md hover:bg-[var(--bg-surface)] shrink-0"
                            data-testid="ref-copy-url"
                        >
                            <Copy size={14} />
                        </button>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-5 text-[13.5px] text-[var(--text-2)]">
                        <div className="flex items-center gap-2">
                            <CheckCircle size={14} weight="fill" className="text-[var(--primary-glow)]" />
                            Referrals sign up via <code className="font-mono text-[var(--primary-bright)]">?ref=YOURCODE</code>
                        </div>
                        <div className="flex items-center gap-2">
                            <CheckCircle size={14} weight="fill" className="text-[var(--primary-glow)]" />
                            10% commission credited in AONE
                        </div>
                        <div className="flex items-center gap-2">
                            <CheckCircle size={14} weight="fill" className="text-[var(--primary-glow)]" />
                            Instant transfer to your wallet
                        </div>
                    </div>
                </div>

                {/* Commission history */}
                <div className="max-w-[1100px] mx-auto mt-8">
                    <div className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-[var(--text-3)] mb-3">
                        Commission History
                    </div>
                    <div className="card overflow-hidden">
                        <table className="tbl" data-testid="ref-history-table">
                            <thead>
                                <tr>
                                    <th>Referee</th>
                                    <th>Plan</th>
                                    <th>USDT Price</th>
                                    <th>Commission (AONE)</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.commissions.length === 0 ? (
                                    <tr><td colSpan={5} className="text-center text-[var(--text-3)] py-6">No commissions yet - share your link to earn</td></tr>
                                ) : data.commissions.map((c, i) => (
                                    <tr key={i}>
                                        <td className="text-[var(--text-2)] font-mono text-[12px]">{c.referee_masked}</td>
                                        <td className="font-medium">{c.plan_name}</td>
                                        <td className="font-mono">${c.plan_usdt.toFixed(2)}</td>
                                        <td className="font-mono text-[var(--primary-glow)]">+{fmtAone(c.commission_aone, 2)}</td>
                                        <td className="text-[var(--text-3)] text-[12px]">{new Date(c.created_at).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </div>
    );
}

function StatCard({ label, value, icon, mono, ...rest }) {
    return (
        <div className="card p-6" {...rest}>
            <div className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-[var(--text-3)] flex items-center gap-1.5">
                {icon}
                {label}
            </div>
            <div className={`text-[24px] font-bold text-[var(--text)] mt-2 ${mono ? "font-mono" : ""} tabular-nums`}>
                {value}
            </div>
        </div>
    );
}
